/**
 * TAB 3 — Games (requirement §22 to §30, AC-10 to AC-12).
 *
 * Game registry and gameplay accounting. Per §23 the Game Owner is deliberately
 * NOT a column here — game performance is reported platform-wide, and the
 * Game Owner cut lives on the Dashboard.
 *
 * Provider is GameCatalogue.type: VE for the in-house Vegas games, TD for Tada.
 */

import * as React from "react";
import {
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
  KpiCard,
  TreasuryFilters,
  DataTable,
  StatusChip,
  SectionTitle,
  ErrorBar,
  Loading,
  ExportButton,
  exportRows,
  fmtVC,
  fmtInt,
  fmtPct,
  fmtDateTime,
  filterInputSx,
  GOLD,
} from "../components/common";
import { getGames, getGameDetail, getGameRounds } from "../api";

const STATUS_OPTIONS = ["all", "Active", "Inactive", "Coming Soon", "Unregistered"];

export const GamesTab = ({ filters, onFiltersChange }) => {
  const [selectedGame, setSelectedGame] = React.useState(null);

  return selectedGame ? (
    <GameDetail
      game={selectedGame}
      filters={filters}
      onFiltersChange={onFiltersChange}
      onBack={() => setSelectedGame(null)}
    />
  ) : (
    <GameList
      filters={filters}
      onFiltersChange={onFiltersChange}
      onOpen={setSelectedGame}
    />
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Games main table (§23, §24)
// ═════════════════════════════════════════════════════════════════════════════

const GameList = ({ filters, onFiltersChange, onOpen }) => {
  const [state, setState] = React.useState({ data: [], pagination: null, summary: null, providers: [] });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [provider, setProvider] = React.useState("all");
  const [status, setStatus] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [sort, setSort] = React.useState({ field: "totalBet", order: "DESC" });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getGames({
          ...filters,
          search,
          provider,
          status,
          page,
          limit,
          sortField: sort.field,
          sortOrder: sort.order,
        });
        if (!cancelled) setState(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters, search, provider, status, page, limit, sort]);

  const columns = [
    { key: "gameId", label: "Game ID", width: 120 },
    { key: "gameName", label: "Game Name", sortable: true },
    {
      key: "provider",
      label: "Provider",
      sortable: true,
      render: (r) => (
        <Chip
          size="small"
          label={r.providerLabel || r.provider || "-"}
          sx={{
            backgroundColor: r.provider === "VE" ? "#1A237E" : r.provider === "TD" ? "#4A148C" : "#333",
            color: "#FFF",
            fontSize: "0.65rem",
          }}
        />
      ),
    },
    { key: "totalBet", label: "Total Bet", align: "right", sortable: true, render: (r) => fmtVC(r.totalBet) },
    { key: "totalWin", label: "Total Win", align: "right", sortable: true, render: (r) => fmtVC(r.totalWin) },
    { key: "totalLoss", label: "Total Loss", align: "right", sortable: true, render: (r) => fmtVC(r.totalLoss) },
    {
      key: "ggr",
      label: "GGR",
      align: "right",
      sortable: true,
      render: (r) => (
        <span style={{ color: r.ggr >= 0 ? "#A5D6A7" : "#EF9A9A" }}>{fmtVC(r.ggr)}</span>
      ),
    },
    { key: "rtp", label: "RTP", align: "right", sortable: true, render: (r) => fmtPct(r.rtp) },
    { key: "activePlayers", label: "Active Players", align: "right", sortable: true, render: (r) => fmtInt(r.activePlayers) },
    { key: "totalRounds", label: "Spin Rounds", align: "right", sortable: true, render: (r) => fmtInt(r.totalRounds) },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusChip value={r.status} /> },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (r) => (
        <Button
          size="small"
          endIcon={<ChevronRightIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(r);
          }}
          sx={{ color: GOLD, textTransform: "none", fontSize: "0.7rem" }}
        >
          View Details
        </Button>
      ),
    },
  ];

  const s = state.summary || {};

  return (
    <Box>
      <TreasuryFilters
        filters={filters}
        onChange={onFiltersChange}
          extra={
          <>
            <FormControl size="small" sx={filterInputSx}>
              <InputLabel>Provider</InputLabel>
              <Select label="Provider" value={provider} onChange={(e) => { setProvider(e.target.value); setPage(1); }}>
                <MenuItem value="all">All Providers</MenuItem>
                {(state.providers || []).map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={filterInputSx}>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                {STATUS_OPTIONS.map((s2) => (
                  <MenuItem key={s2} value={s2}>
                    {s2 === "all" ? "All Statuses" : s2}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Search Game ID / Name"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              sx={{ ...filterInputSx, minWidth: 210 }}
            />
            <ExportButton
              disabled={!state.data?.length}
              onExport={(format) =>
                exportRows(
                  state.data.map((r) => ({
                    "Game ID": r.gameId,
                    "Game Name": r.gameName,
                    Provider: r.providerLabel,
                    "Total Bet": r.totalBet,
                    "Total Win": r.totalWin,
                    "Total Loss": r.totalLoss,
                    GGR: r.ggr,
                    "RTP %": r.rtp,
                    "Active Players": r.activePlayers,
                    "Spin Rounds": r.totalRounds,
                    Status: r.status,
                  })),
                  "house-treasury-games",
                  format
                )
              }
            />
          </>
        }
      />
      <ErrorBar error={error} onClose={() => setError("")} />

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}><KpiCard dense label="GAMES" value={fmtInt(s.games)} /></Grid>
        <Grid item xs={6} md={2}><KpiCard dense label="TOTAL BET" value={fmtVC(s.totalBet)} suffix="VC" /></Grid>
        <Grid item xs={6} md={2}><KpiCard dense label="TOTAL WIN" value={fmtVC(s.totalWin)} suffix="VC" /></Grid>
        <Grid item xs={6} md={2}><KpiCard dense label="GGR" value={fmtVC(s.ggr)} suffix="VC" accent={GOLD} /></Grid>
        <Grid item xs={6} md={2}><KpiCard dense label="RTP" value={fmtPct(s.rtp)} /></Grid>
        <Grid item xs={6} md={2}><KpiCard dense label="ROUNDS" value={fmtInt(s.totalRounds)} /></Grid>
      </Grid>

      <DataTable
        columns={columns}
        rows={state.data}
        loading={loading}
        pagination={state.pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        sortField={sort.field}
        sortOrder={sort.order}
        onSortChange={(field, order) => setSort({ field, order })}
        onRowClick={onOpen}
        emptyMessage="No games found."
      />
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Game detail + round audit (§25 to §27)
// ═════════════════════════════════════════════════════════════════════════════

const GameDetail = ({ game, filters, onFiltersChange, onBack }) => {
  const [detail, setDetail] = React.useState(null);
  const [rounds, setRounds] = React.useState({ data: [], pagination: null, note: "" });
  const [loading, setLoading] = React.useState(true);
  const [roundsLoading, setRoundsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(25);
  const [searchFields, setSearchFields] = React.useState({
    roundId: "",
    playerId: "",
    txnId: "",
    betId: "",
  });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getGameDetail({ ...filters, gameId: game.gameId });
        if (!cancelled) setDetail(result.game);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [game.gameId, filters]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setRoundsLoading(true);
      try {
        const result = await getGameRounds({
          ...filters,
          gameId: game.gameId,
          ...searchFields,
          page,
          limit,
        });
        if (!cancelled) setRounds(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setRoundsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [game.gameId, filters, searchFields, page, limit]);

  const d = detail || {};

  const roundColumns = [
    { key: "occurredAt", label: "Time", render: (r) => fmtDateTime(r.occurredAt) },
    { key: "roundId", label: "Round ID", width: 180 },
    { key: "playerId", label: "Player ID", width: 130 },
    { key: "bet", label: "Bet", align: "right", render: (r) => fmtVC(r.bet) },
    { key: "win", label: "Win", align: "right", render: (r) => fmtVC(r.win) },
    { key: "loss", label: "Loss", align: "right", render: (r) => fmtVC(r.loss) },
    {
      key: "ggr",
      label: "GGR",
      align: "right",
      render: (r) => (
        <span style={{ color: r.ggr >= 0 ? "#A5D6A7" : "#EF9A9A" }}>{fmtVC(r.ggr)}</span>
      ),
    },
    { key: "status", label: "Status", render: (r) => <StatusChip value={r.status} /> },
    {
      key: "transactionIds",
      label: "Transaction ID",
      width: 220,
      render: (r) => (
        <Box sx={{ fontSize: "0.68rem", color: "#BDBDBD" }}>
          {(r.transactionIds || []).join(", ") || "-"}
        </Box>
      ),
    },
  ];

  const setField = (key, value) => {
    setSearchFields((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: GOLD, textTransform: "none" }}>
          Games
        </Button>
        <Typography sx={{ color: "#FFF", fontWeight: 700 }}>{game.gameName}</Typography>
        <Chip
          size="small"
          label={game.providerLabel || game.provider}
          sx={{ backgroundColor: "#242426", color: GOLD, fontSize: "0.65rem" }}
        />
      </Box>

      <TreasuryFilters filters={filters} onChange={onFiltersChange} />
      <ErrorBar error={error} onClose={() => setError("")} />

      {loading && !detail ? (
        <Loading />
      ) : (
        // Seven cards laid out as a flex row that divides the width evenly and
        // wraps. They were md={12/7}, which is not a valid MUI grid width and
        // overflowed the page.
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
          <Box sx={{ flex: "1 1 150px", minWidth: 150 }}><KpiCard dense label="TOTAL BETS" value={fmtVC(d.totalBet)} suffix="VC" /></Box>
          <Box sx={{ flex: "1 1 150px", minWidth: 150 }}><KpiCard dense label="TOTAL WINS" value={fmtVC(d.totalWin)} suffix="VC" /></Box>
          <Box sx={{ flex: "1 1 150px", minWidth: 150 }}><KpiCard dense label="TOTAL LOSS" value={fmtVC(d.totalLoss)} suffix="VC" /></Box>
          <Box sx={{ flex: "1 1 150px", minWidth: 150 }}><KpiCard dense label="GGR" value={fmtVC(d.ggr)} suffix="VC" accent={GOLD} /></Box>
          <Box sx={{ flex: "1 1 150px", minWidth: 150 }}><KpiCard dense label="RTP" value={fmtPct(d.rtp)} /></Box>
          <Box sx={{ flex: "1 1 150px", minWidth: 150 }}><KpiCard dense label="TOTAL ROUNDS" value={fmtInt(d.totalRounds)} /></Box>
          <Box sx={{ flex: "1 1 150px", minWidth: 150 }}><KpiCard dense label="ACTIVE PLAYERS" value={fmtInt(d.activePlayers)} /></Box>
        </Box>
      )}

      <SectionTitle
        action={
          <ExportButton
            disabled={!rounds.data?.length}
            onExport={(format) =>
              exportRows(
                rounds.data.map((r) => ({
                  Time: r.occurredAt,
                  "Round ID": r.roundId,
                  "Player ID": r.playerId,
                  Bet: r.bet,
                  Win: r.win,
                  Loss: r.loss,
                  GGR: r.ggr,
                  Status: r.status,
                  "Transaction IDs": (r.transactionIds || []).join(" | "),
                })),
                `house-treasury-game-${game.gameId}-rounds`,
                format
              )
            }
          />
        }
      >
        Spin / Round Audit
      </SectionTitle>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <TextField size="small" label="Round ID" value={searchFields.roundId}
          onChange={(e) => setField("roundId", e.target.value)} sx={filterInputSx} />
        <TextField size="small" label="Player ID" value={searchFields.playerId}
          onChange={(e) => setField("playerId", e.target.value)} sx={filterInputSx} />
        <TextField size="small" label="Transaction ID" value={searchFields.txnId}
          onChange={(e) => setField("txnId", e.target.value)} sx={filterInputSx} />
        <TextField size="small" label="Bet ID" value={searchFields.betId}
          onChange={(e) => setField("betId", e.target.value)} sx={filterInputSx} />
      </Box>

      <DataTable
        dense
        columns={roundColumns}
        rows={rounds.data}
        loading={roundsLoading}
        pagination={rounds.pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        emptyMessage="No rounds recorded for this game in the selected period."
      />

      {rounds.note && (
        <Typography sx={{ color: "#666", fontSize: "0.65rem", mt: 1 }}>{rounds.note}</Typography>
      )}
    </Box>
  );
};

export default GamesTab;
